// src/core/count.js
export function MotionFlowCount(userOptions = {}) {
  let items = [];
  let ticking = false;
  let initialized = false;

  let scrollHandler = null;
  let resizeHandler = null;

  /* ==========================================================
     ACCESSIBILITY
  ========================================================== */
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  /* ==========================================================
     DEFAULTS
  ========================================================== */
  const defaults = {
    from: 0,
    once: false,
    repeat: "both",
    duration: 1200,
    trigger: "top 90%",
  };

  const cfg = { ...defaults, ...(userOptions.count || {}) };

  /* ==========================================================
     HELPERS
  ========================================================== */
  function parseTrigger(str) {
    if (!str) return { elementAnchor: "top", viewportAnchor: 90 };

    const parts = String(str).trim().toLowerCase().split(/\s+/);
    const map = { top: 0, center: 50, bottom: 100 };

    return {
      elementAnchor: parts[0] || "top",
      viewportAnchor: parts[1]?.endsWith("%")
        ? parseInt(parts[1], 10)
        : map[parts[1]] ?? 90,
    };
  }

  function getAnchor(rect, anchor) {
    if (anchor === "center") return rect.top + rect.height / 2;
    if (anchor === "bottom") return rect.bottom;
    return rect.top;
  }

  function toNumber(raw) {
    const n = parseFloat(String(raw).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }

  function getDecimals(raw) {
    const s = String(raw);
    const p = s.split(".");
    return p[1] ? p[1].length : 0;
  }

  function detectNumberFormat(raw) {
    const str = String(raw).trim();
    const usPattern = /^\d{1,3}(,\d{3})+(\.\d+)?$/;
    const indianPattern = /^\d{1,3}(,\d{2})+(,\d{3})(\.\d+)?$/;

    if (usPattern.test(str)) return "us";
    if (indianPattern.test(str)) return "in";
    return "plain";
  }

  function format(n, locale, decimals, useGrouping = true) {
    return Number(n).toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping,
    });
  }

  function stopAnim(item) {
    item.animating = false;
    if (item.rafId) cancelAnimationFrame(item.rafId);
    item.rafId = null;
    item.startTime = null;
  }

  /* ==========================================================
     COLLECT
  ========================================================== */
  function collect() {
    const existing = new Map(items.map(it => [it.el, it]));
    const nodes = document.querySelectorAll("[data-mf-count-to]");
    const next = [];

    nodes.forEach(el => {
      const rawTarget = el.getAttribute("data-mf-count-to");
      if (!rawTarget) return;

      const target = toNumber(rawTarget);
      if (target === null) return;

      const fromRaw = el.dataset.mfCountFrom;
      const from =
        fromRaw !== undefined
          ? toNumber(fromRaw) ?? 0
          : cfg.from ?? 0;

      const toDecimals = getDecimals(rawTarget);
      const fromDecimals = getDecimals(fromRaw || "");
      const animDecimals = Math.max(toDecimals, fromDecimals);

      const formatType = detectNumberFormat(rawTarget);

      let locale;
      let useGrouping = true;

      if (formatType === "us") locale = "en-US";
      else if (formatType === "in") locale = "en-IN";
      else useGrouping = false;

      const duration = Number(el.dataset.mfCountDuration ?? cfg.duration);

      const once =
        el.dataset.mfCountOnce !== undefined
          ? el.dataset.mfCountOnce === "true" || el.dataset.mfCountOnce === ""
          : cfg.once;

      const repeat = String(
        el.dataset.mfCountRepeat ?? cfg.repeat
      ).toLowerCase();

      const trigger = parseTrigger(
        el.dataset.mfCountTrigger ?? cfg.trigger
      );

      const prev = existing.get(el);

      if (prev) {
        Object.assign(prev, {
          from,
          target,
          locale,
          useGrouping,
          duration,
          once,
          repeat,
          trigger,
          fromDecimals,
          toDecimals,
          animDecimals,
        });
        next.push(prev);
        existing.delete(el);
      } else {
        el.textContent = format(from, locale, fromDecimals, useGrouping);

        next.push({
          el,
          from,
          target,
          locale,
          useGrouping,
          duration,
          once,
          repeat,
          trigger,
          fromDecimals,
          toDecimals,
          animDecimals,
          started: false,
          animating: false,
          startTime: null,
          rafId: null,
        });
      }
    });

    existing.forEach(stopAnim);
    items = next;
  }

  /* ==========================================================
     ANIMATION
  ========================================================== */
  function animate(ts, item) {
    if (!item.animating) return;

    if (!item.startTime) item.startTime = ts;

    const p = Math.min((ts - item.startTime) / item.duration, 1);
    const rawValue = item.from + (item.target - item.from) * p;

    const value =
      item.animDecimals > 0
        ? Number(rawValue.toFixed(item.animDecimals))
        : rawValue;

    item.el.textContent = format(
      value,
      item.locale,
      item.animDecimals,
      item.useGrouping
    );

    if (p < 1) {
      item.rafId = requestAnimationFrame(n => animate(n, item));
    } else {
      item.animating = false;
      item.el.textContent = format(
        item.target,
        item.locale,
        item.toDecimals,
        item.useGrouping
      );
    }
  }

  /* ==========================================================
     UPDATE
  ========================================================== */
  function update() {
    const vh = window.innerHeight;

    items.forEach(item => {
      const rect = item.el.getBoundingClientRect();
      const elementPos = getAnchor(rect, item.trigger.elementAnchor);
      const viewportPos = (vh * item.trigger.viewportAnchor) / 100;

      const visible = elementPos < viewportPos && rect.bottom > 0;
      const above = rect.bottom <= 0;
      const below = rect.top >= vh;

      if (visible && !item.started) {
        item.started = true;

        if (reduceMotion) {
          item.el.textContent = format(
            item.target,
            item.locale,
            item.toDecimals,
            item.useGrouping
          );
        } else {
          stopAnim(item);
          item.animating = true;
          item.startTime = null;
          item.rafId = requestAnimationFrame(ts => animate(ts, item));
        }
      }

      if (!item.once && item.started) {
        const reset =
          (item.repeat === "top" && above) ||
          (item.repeat === "bottom" && below) ||
          (item.repeat === "both" && (above || below));

        if (reset) {
          stopAnim(item);
          item.started = false;
          item.el.textContent = format(
            item.from,
            item.locale,
            item.fromDecimals,
            item.useGrouping
          );
        }
      }
    });

    ticking = false;
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  /* ==========================================================
     LIFECYCLE (IDEMPOTENT)
  ========================================================== */
  function init() {
    if (initialized) {
      destroy(false);
    }

    initialized = true;
    collect();
    update();

    scrollHandler = onScroll;
    resizeHandler = onScroll;

    window.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", resizeHandler);
  }

  function refresh() {
    collect();
    onScroll();
  }

  function destroy(reset = true) {
    initialized = false;

    window.removeEventListener("scroll", scrollHandler);
    window.removeEventListener("resize", resizeHandler);

    items.forEach(item => {
      stopAnim(item);
      if (reset) {
        item.el.textContent = format(
          item.from,
          item.locale,
          item.fromDecimals,
          item.useGrouping
        );
      }
    });

    items = [];
    ticking = false;
  }

  init();
  return { refresh, destroy };
}
